/** Renders the virtualized, metadata-first rail for a brand-item cohort. */
import {Box, ButtonBase, Chip, Skeleton, Stack, Typography} from "@mui/material";
import {Virtuoso} from "react-virtuoso";
import {
   DataViewColumn,
   formatDataViewValue
} from "../../../components/dataview/DataViewColumns";
import {BrandItemBoostChip} from "../BrandItemBoostChip";
import {CohortRow, localizedValue} from "./BrandItemCohort";
import {DashboardDrilldownItem} from "./useBrandItemCohortItems";

interface BrandItemDrilldownListProps {
   rows: CohortRow[];
   columns: DataViewColumn[];
   itemsById: Map<string, DashboardDrilldownItem>;
   selectedId?: string;
   loading: boolean;
   disabled: boolean;
   onSelect: (id: string) => void;
}

/** Returns the compact product label brands use to scan the rail. */
const itemLabel = (item: DashboardDrilldownItem): string => localizedValue(
   item.resolved?.nameLocalized,
   typeof navigator === "undefined" ? undefined : navigator.language,
   item.resolved?.itemSku || `Item ${item.itemNr}`
);

/** Shows one cohort item with product context and the strongest metadata signals. */
const CohortRailRow = ({
   row,
   columns,
   item,
   selected,
   loading,
   disabled,
   onSelect
}: {
   row: CohortRow;
   columns: DataViewColumn[];
   item?: DashboardDrilldownItem;
   selected: boolean;
   loading: boolean;
   disabled: boolean;
   onSelect: (id: string) => void;
}) => {
   const evidence = columns
      .filter(column => row.values[column.key] !== null && row.values[column.key] !== undefined)
      .slice(0, 3);
   const label = item ? itemLabel(item) : loading ? "Loading item…" : "Item unavailable";
   const price = item?.resolved?.sellingPrices?.find(candidate => !!candidate);
   const hasOpenRequest = item?.resolved?.problems?.some(problem => problem && !problem.solved);
   const locale = typeof navigator === "undefined" ? undefined : navigator.language;
   const size = localizedValue(item?.resolved?.sizeLocalized, locale, "");
   const color = localizedValue(item?.resolved?.colorLocalized, locale, "");

   return <ButtonBase
      component="button"
      disabled={disabled}
      aria-current={selected ? "true" : undefined}
      onClick={() => onSelect(row.id)}
      onKeyDown={event => {
         if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(row.id);
         }
      }}
      sx={{
         width: "100%",
         minHeight: 104,
         alignItems: "stretch",
         justifyContent: "stretch",
         textAlign: "left",
         borderLeft: 4,
         borderColor: selected ? "primary.main" : "transparent",
         borderBottom: 1,
         borderBottomColor: "divider",
         backgroundColor: selected ? "action.selected" : "background.paper",
         "&:hover": {backgroundColor: selected ? "action.selected" : "action.hover"},
         "&:focus-visible": {outline: "3px solid", outlineColor: "primary.light", outlineOffset: -3}
      }}
   >
      <Stack direction="row" spacing={1.5} sx={{width: "100%", p: 1.5}}>
         {item?.resolved?.imageUrls?.[0]
            ? <Box
               component="img"
               src={item.resolved.imageUrls[0]}
               alt=""
               sx={{width: 64, height: 80, objectFit: "cover", bgcolor: "grey.100", flexShrink: 0}}
            />
            : <Skeleton variant="rectangular" width={64} height={80} sx={{flexShrink: 0}}/>}
         <Stack spacing={0.5} sx={{minWidth: 0, flex: 1}}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
               <Box sx={{minWidth: 0}}>
                  <Typography variant="subtitle2" noWrap>{label}</Typography>
                  <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                     <Typography variant="caption" color="text.secondary" noWrap>
                        {item ? `Item #${item.itemNr}` : row.id}
                     </Typography>
                     {size && <Typography variant="caption" color="text.secondary">
                        Size: <Box component="span" color="text.primary">{size}</Box>
                     </Typography>}
                     {color && <Typography variant="caption" color="text.secondary">
                        Color: <Box component="span" color="text.primary">{color}</Box>
                     </Typography>}
                  </Stack>
               </Box>
               {price && <Typography variant="subtitle2" sx={{whiteSpace: "nowrap"}}>
                  {price.value} {price.currency}
               </Typography>}
            </Stack>
            <Stack direction="row" spacing={0.25} useFlexGap flexWrap="wrap">
               {evidence.map(column => <Chip
                  key={column.key}
                  size="small"
                  variant="outlined"
                  label={`${column.label}: ${formatDataViewValue(row.values[column.key], column.dataType)}`}
                  sx={{
                     height: 18,
                     borderRadius: 0.75,
                     fontSize: "0.65rem",
                     "& .MuiChip-label": {px: 0.25}
                  }}
               />)}
            </Stack>
            {item?.resolved?.activeBoost && <BrandItemBoostChip boost={item.resolved.activeBoost} compact/>}
            {hasOpenRequest && <Chip
               size="small"
               color="warning"
               variant="outlined"
               label="Action requested"
               sx={{alignSelf: "flex-start", height: 22}}
            />}
         </Stack>
      </Stack>
   </ButtonBase>;
};

/** Virtualizes the complete cohort while preserving selection and keyboard semantics. */
export const BrandItemDrilldownList = ({
   rows,
   columns,
   itemsById,
   selectedId,
   loading,
   disabled,
   onSelect
}: BrandItemDrilldownListProps) => <Virtuoso
   data={rows}
   style={{height: "100%"}}
   itemContent={(_index, row) => <CohortRailRow
      row={row}
      columns={columns}
      item={itemsById.get(row.id)}
      selected={row.id === selectedId}
      loading={loading}
      disabled={disabled}
      onSelect={onSelect}
   />}
/>;
